import React from "react";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Modal,
  IconButton,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import EmployeeAssetsVM, {
  MAX_REASON_LEN,
  MAX_NOTES_LEN,
  RETURN_CONDITIONS,
} from "../viewmodel/EmployeeAssetsVM";

// created_at (seconds) -> "dd/mm/yyyy, hh:mm am/pm"
const formatDateTime = (value) => {
  if (!value || isNaN(Number(value))) return "-";
  return new Date(Number(value) * 1000).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const holderLabel = (h) =>
  h
    ? `${h.employee_name}${h.employee_code ? ` (${h.employee_code})` : ""} – ${
        h.asset_count
      } asset(s)`
    : "";

const headCell = {
  fontWeight: "bold",
  backgroundColor: "#052660",
  color: "#fff",
  fontSize: "0.875rem",
  padding: "10px 16px",
  whiteSpace: "nowrap",
};

const EmployeeAssetsPanel = ({ active, onReturned }) => {
  const {
    doneMessage,
    holders,
    selectedHolder,
    assets,
    selectedIds,
    selectedAssets,
    loadingHolders,
    loadingAssets,
    returnOpen,
    reason,
    setReason,
    reasonTouched,
    setReasonTouched,
    reasonValid,
    conditions,
    setAssetCondition,
    setAssetNotes,
    markAllGood,
    allConditionsSet,
    saving,
    handleHolderChange,
    toggleAsset,
    toggleAll,
    openReturn,
    closeReturn,
    confirmReturn,
  } = EmployeeAssetsVM({ active, onReturned });

  const allSelected = assets.length > 0 && selectedIds.length === assets.length;
  const someSelected = selectedIds.length > 0 && !allSelected;
  const returnLabel = allSelected
    ? `Return All (${selectedIds.length})`
    : `Return Selected (${selectedIds.length})`;

  return (
    <Grid container sx={{ width: "100%", pb: "2.5em" }}>
      {/* 1. Choose the employee */}
      <Grid item xs={12} md={6}>
        <Autocomplete
          options={holders}
          value={selectedHolder}
          onChange={(e, value) => handleHolderChange(value)}
          getOptionLabel={holderLabel}
          isOptionEqualToValue={(o, v) =>
            o.employee_name === v.employee_name &&
            (o.employee_code || "") === (v.employee_code || "")
          }
          loading={loadingHolders}
          noOptionsText="No employee has an asset right now"
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Employee"
              placeholder="Type a name to search"
              helperText="Only employees who have assets right now are listed"
            />
          )}
        />
      </Grid>

      {/* 2. Their assets */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        {!selectedHolder && doneMessage ? (
          <Typography sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.95rem" }}>
            ✓ {doneMessage}
          </Typography>
        ) : !selectedHolder ? (
          <Typography sx={{ color: "#666", fontSize: "0.9rem" }}>
            Choose an employee to see all the assets they have. Useful when someone
            leaves the company.
          </Typography>
        ) : loadingAssets ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : assets.length === 0 ? (
          <Typography sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.95rem" }}>
            ✓ {selectedHolder.employee_name} has no assets now.
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
                mb: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600, color: "#052660" }}>
                {selectedHolder.employee_name} has {assets.length} asset(s)
              </Typography>
              <Button
                variant="contained"
                color="error"
                startIcon={<AssignmentReturnIcon />}
                onClick={openReturn}
                disabled={selectedIds.length === 0}
              >
                {returnLabel}
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCell, width: 48 }} padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleAll}
                        sx={{ color: "#fff", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#fff" } }}
                      />
                    </TableCell>
                    <TableCell sx={headCell}>Asset ID</TableCell>
                    <TableCell sx={headCell}>Category</TableCell>
                    <TableCell sx={headCell}>Brand</TableCell>
                    <TableCell sx={headCell}>Serial No</TableCell>
                    <TableCell sx={headCell}>Assigned on</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assets.map((a) => (
                    <TableRow
                      key={a.asset_id}
                      hover
                      onClick={() => toggleAsset(a.asset_id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.includes(a.asset_id)} />
                      </TableCell>
                      <TableCell>
                        <a
                          href={`/asset?id=${a.asset_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#052660" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {a.asset_id}
                        </a>
                      </TableCell>
                      <TableCell>{a.category || "-"}</TableCell>
                      <TableCell>{a.brand || "-"}</TableCell>
                      <TableCell>{a.laptop_desktop_serial_no || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatDateTime(a.assigned_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Grid>

      {/* 3. Reason popup */}
      <Modal
        open={returnOpen}
        onClose={closeReturn}
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "5px",
            boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.064)",
            p: "20px",
            width: "680px",
            maxWidth: "92vw",
            maxHeight: "88vh",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <IconButton
            onClick={closeReturn}
            sx={{ position: "absolute", top: 8, right: 8, color: "#555" }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Return Assets
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Employee:</b> {selectedHolder?.employee_name}
            {selectedHolder?.employee_code ? ` (${selectedHolder.employee_code})` : ""}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography variant="body2">
              <b>Assets to return ({selectedAssets.length}) – choose the condition of each:</b>
            </Typography>
            <Button size="small" onClick={markAllGood} disabled={saving}>
              Mark all Good
            </Button>
          </Box>
          <TableContainer
            sx={{ border: "1px solid #eee", borderRadius: "4px", mb: 2, maxHeight: 260 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Asset</TableCell>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5", width: 170 }}>
                    Condition *
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAssets.map((a) => {
                  const c = conditions[a.asset_id] || {};
                  return (
                    <TableRow key={a.asset_id}>
                      <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                        <b>{a.category || "Asset"}</b>
                        <div style={{ color: "#666" }}>{a.asset_id}</div>
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={c.condition || ""}
                          onChange={(e) => setAssetCondition(a.asset_id, e.target.value)}
                          disabled={saving}
                          SelectProps={{ displayEmpty: true }}
                          error={!c.condition}
                        >
                          <MenuItem value="" disabled>
                            <em>Select</em>
                          </MenuItem>
                          {RETURN_CONDITIONS.map((rc) => (
                            <MenuItem key={rc} value={rc}>
                              {rc}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Optional"
                          value={c.notes || ""}
                          onChange={(e) => setAssetNotes(a.asset_id, e.target.value)}
                          disabled={saving}
                          inputProps={{ maxLength: MAX_NOTES_LEN }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TextField
            label="Reason for return"
            placeholder="e.g. Employee left the organization"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setReasonTouched(true)}
            multiline
            rows={3}
            fullWidth
            required
            disabled={saving}
            inputProps={{ maxLength: MAX_REASON_LEN }}
            error={reasonTouched && !reasonValid}
            helperText={
              reasonTouched && !reasonValid ? "Please enter a reason to return the assets" : " "
            }
          />
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "right", color: "#888" }}
          >
            {reason.length} / {MAX_REASON_LEN}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", color: "#666", mt: 1 }}>
            All selected assets become Available (an asset at repair stays Maintenance). The
            employee gets one email with the list.
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
            <Button variant="contained" color="secondary" onClick={closeReturn} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmReturn}
              disabled={!reasonValid || !allConditionsSet || saving}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                `Return (${selectedAssets.length})`
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Grid>
  );
};

export default EmployeeAssetsPanel;